import { optimizedCompletion } from '@/lib/openai/speedOptimizer';
import { ApiError } from '@/lib/errors';

export interface AudienceTarget {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'experience' | 'gender' | 'diversity' | 'generation' | 'industry';
  languageAdjustments: {
    tone: string;
    keywords: string[];
    avoidWords: string[];
    emphasize: string[];
  };
}

export const AUDIENCE_TARGETS: AudienceTarget[] = [
  // Experience Level Targeting
  {
    id: 'graduates',
    name: 'Recent Graduates',
    description: 'Entry-level candidates fresh from university',
    icon: '🎓',
    category: 'experience',
    languageAdjustments: {
      tone: 'encouraging, growth-focused, mentorship-oriented',
      keywords: ['entry-level', 'training provided', 'mentorship', 'career development', 'learning opportunities'],
      avoidWords: ['extensive experience', 'proven track record', 'industry veteran'],
      emphasize: ['growth potential', 'learning culture', 'support system', 'career progression']
    }
  },
  {
    id: 'professionals',
    name: 'Experienced Professionals',
    description: 'Mid to senior-level candidates with established careers',
    icon: '💼',
    category: 'experience',
    languageAdjustments: {
      tone: 'professional, achievement-focused, results-oriented',
      keywords: ['proven experience', 'leadership', 'strategic thinking', 'expertise', 'track record'],
      avoidWords: ['entry-level', 'basic training', 'learning the ropes'],
      emphasize: ['impact', 'leadership opportunities', 'strategic role', 'expertise valued']
    }
  },
  {
    id: 'career_changers',
    name: 'Career Changers',
    description: 'Professionals transitioning between industries or roles',
    icon: '🔄',
    category: 'experience',
    languageAdjustments: {
      tone: 'welcoming, transferable-skills focused, opportunity-oriented',
      keywords: ['transferable skills', 'diverse background', 'fresh perspective', 'adaptability'],
      avoidWords: ['industry-specific experience required', 'must have X years in field'],
      emphasize: ['skill diversity', 'fresh perspectives', 'adaptability', 'cross-functional experience']
    }
  },

  // Gender Inclusive Targeting
  {
    id: 'women_friendly',
    name: 'Women-Inclusive',
    description: 'Language that appeals to and includes women',
    icon: '👩‍💼',
    category: 'gender',
    languageAdjustments: {
      tone: 'inclusive, collaborative, supportive',
      keywords: ['collaborative', 'inclusive environment', 'work-life balance', 'supportive team', 'flexible'],
      avoidWords: ['rockstar', 'ninja', 'guru', 'aggressive', 'dominant'],
      emphasize: ['team collaboration', 'inclusive culture', 'professional development', 'supportive environment']
    }
  },
  {
    id: 'gender_neutral',
    name: 'Gender Neutral',
    description: 'Completely gender-neutral language',
    icon: '⚖️',
    category: 'gender',
    languageAdjustments: {
      tone: 'neutral, professional, merit-based',
      keywords: ['professional', 'qualified candidate', 'team member', 'contributor'],
      avoidWords: ['guys', 'chairman', 'manpower', 'he/she'],
      emphasize: ['qualifications', 'merit-based', 'equal opportunity', 'professional growth']
    }
  },

  // Diversity & Inclusion
  {
    id: 'diverse_backgrounds',
    name: 'Diverse Backgrounds',
    description: 'Welcoming to candidates from all backgrounds',
    icon: '🌍',
    category: 'diversity',
    languageAdjustments: {
      tone: 'inclusive, welcoming, diversity-celebrating',
      keywords: ['diverse perspectives', 'inclusive workplace', 'all backgrounds welcome', 'equal opportunity'],
      avoidWords: ['cultural fit', 'native speaker', 'traditional background'],
      emphasize: ['diversity valued', 'inclusive environment', 'equal opportunities', 'varied perspectives']
    }
  },
  {
    id: 'neurodivergent',
    name: 'Neurodivergent Friendly',
    description: 'Inclusive for neurodivergent candidates',
    icon: '🧠',
    category: 'diversity',
    languageAdjustments: {
      tone: 'clear, structured, accommodation-aware',
      keywords: ['clear expectations', 'structured environment', 'accommodations available', 'diverse thinking'],
      avoidWords: ['fast-paced', 'high-pressure', 'must be outgoing', 'networking required'],
      emphasize: ['clear processes', 'structured work', 'accommodation support', 'diverse thinking valued']
    }
  },

  // Generational Targeting
  {
    id: 'gen_z',
    name: 'Gen Z',
    description: 'Appeals to Gen Z values and communication style',
    icon: '📱',
    category: 'generation',
    languageAdjustments: {
      tone: 'authentic, purpose-driven, tech-savvy, flexible',
      keywords: ['purpose-driven', 'social impact', 'flexible work', 'authentic culture', 'innovation'],
      avoidWords: ['traditional office', 'rigid schedule', 'pay your dues'],
      emphasize: ['company purpose', 'social impact', 'flexibility', 'authentic culture', 'growth opportunities']
    }
  },
  {
    id: 'millennials',
    name: 'Millennials',
    description: 'Resonates with millennial priorities',
    icon: '💻',
    category: 'generation',
    languageAdjustments: {
      tone: 'growth-focused, work-life balance, meaningful work',
      keywords: ['career growth', 'work-life balance', 'meaningful work', 'professional development'],
      avoidWords: ['entry-level only', 'no remote work', 'limited growth'],
      emphasize: ['career progression', 'work-life balance', 'meaningful impact', 'development opportunities']
    }
  },

  // Industry Specific
  {
    id: 'tech_startup',
    name: 'Tech Startup Culture',
    description: 'Appeals to startup and tech enthusiasts',
    icon: '🚀',
    category: 'industry',
    languageAdjustments: {
      tone: 'innovative, fast-paced, disruptive, dynamic',
      keywords: ['cutting-edge', 'innovative', 'disruptive', 'fast-paced', 'startup culture'],
      avoidWords: ['traditional', 'conservative', 'slow-moving', 'bureaucratic'],
      emphasize: ['innovation', 'rapid growth', 'cutting-edge tech', 'startup energy']
    }
  },
  {
    id: 'corporate_professional',
    name: 'Corporate Professional',
    description: 'Appeals to traditional corporate professionals',
    icon: '🏢',
    category: 'industry',
    languageAdjustments: {
      tone: 'professional, stable, established, structured',
      keywords: ['established company', 'professional environment', 'structured growth', 'stability'],
      avoidWords: ['disruptive', 'move fast and break things', 'startup chaos'],
      emphasize: ['stability', 'professional development', 'established processes', 'career security']
    }
  }
];

export interface AudienceOptimizationRequest {
  originalJobDescription: string;
  targetAudiences: string[]; // Array of audience IDs
  jobTitle: string;
  company: string;
  industry?: string;
  experienceLevel?: string;
}

export interface AudienceOptimizationResult {
  optimizedJobDescription: string;
  changes: Array<{
    section: string;
    original: string;
    optimized: string;
    reason: string;
    audienceTarget: string;
  }>;
  audienceScore: number; // 0-100 how well it appeals to target audience
  inclusivityScore: number; // 0-100 how inclusive the language is
  recommendations: string[];
  summary: string;
}

export async function optimizeJobDescriptionForAudience(
  request: AudienceOptimizationRequest
): Promise<AudienceOptimizationResult> {
  try {
    if (!request.targetAudiences.length) {
      throw new Error('At least one target audience must be specified');
    }

    const selectedAudiences = AUDIENCE_TARGETS.filter(audience => 
      request.targetAudiences.includes(audience.id)
    );

    if (selectedAudiences.length === 0) {
      throw new Error('No valid target audiences found');
    }

    const prompt = createAudienceOptimizationPrompt(request, selectedAudiences);
    
    const response = await optimizedCompletion(
      'audience-targeting',
      [
        {
          role: 'system',
          content: `You are an expert HR copywriter specializing in inclusive job description optimization. Your task is to optimize job descriptions for specific target audiences while preserving ALL original content sections and maintaining professional quality. Always return the COMPLETE optimized job description with the same structure and length as the original. Return valid JSON only.`
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      { useCache: true }
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No response from AI');
    }

    // Simple JSON cleaning
    let cleanedContent = content
      .replace(/```json\n?/g, '')
      .replace(/```\n?/g, '')
      .trim();

    // Find JSON boundaries if there's extra text
    const jsonStart = cleanedContent.indexOf('{');
    const jsonEnd = cleanedContent.lastIndexOf('}');
    
    if (jsonStart !== -1 && jsonEnd !== -1 && jsonStart < jsonEnd) {
      cleanedContent = cleanedContent.substring(jsonStart, jsonEnd + 1);
    }
    
    try {
      const result: AudienceOptimizationResult = JSON.parse(cleanedContent);
      
      // Validate and fix the response structure
      if (!result.optimizedJobDescription) {
        result.optimizedJobDescription = request.originalJobDescription;
      }
      
      if (typeof result.audienceScore !== 'number') {
        result.audienceScore = 75;
      }
      
      if (typeof result.inclusivityScore !== 'number') {
        result.inclusivityScore = 80;
      }
      
      if (!Array.isArray(result.changes)) {
        result.changes = [];
      }
      
      if (!Array.isArray(result.recommendations)) {
        result.recommendations = [];
      }
      
      if (!result.summary) {
        result.summary = 'Job description optimized for target audience';
      }
      
      return result;
    } catch (parseError) {
      console.error('JSON parsing error:', parseError);
      console.error('Raw content length:', content.length);
      console.error('Raw content preview:', content.substring(0, 500) + '...');
      
      // Return a fallback response instead of throwing an error
      return {
        optimizedJobDescription: request.originalJobDescription,
        changes: [],
        audienceScore: 70,
        inclusivityScore: 75,
        recommendations: [
          "Consider using more inclusive language",
          "Review job requirements for potential bias",
          "Add company culture information"
        ],
        summary: "Unable to fully optimize - original job description returned with basic recommendations"
      };
    }
  } catch (error) {
    console.error('Audience optimization error:', error);
    throw new ApiError(
      error instanceof Error 
        ? `Audience optimization failed: ${error.message}` 
        : 'Failed to optimize job description for target audience', 
      500
    );
  }
}

function createAudienceOptimizationPrompt(
  request: AudienceOptimizationRequest,
  selectedAudiences: AudienceTarget[]
): string {
  const audienceDetails = selectedAudiences.map(audience => 
    `${audience.name}: ${audience.languageAdjustments.tone} tone, emphasize ${audience.languageAdjustments.emphasize.join(', ')}, avoid ${audience.languageAdjustments.avoidWords.join(', ')}`
  ).join('\n');

  return `You are optimizing a job description for specific target audiences. Your task is to:

1. PRESERVE the complete structure and all content sections of the original job description
2. ONLY modify language, tone, and phrasing to appeal to the target audiences
3. DO NOT remove or shorten any sections
4. DO NOT change the core job requirements or responsibilities
5. MAINTAIN the professional quality and completeness

TARGET AUDIENCES:
${audienceDetails}

JOB TITLE: ${request.jobTitle}
COMPANY: ${request.company}

ORIGINAL JOB DESCRIPTION (COMPLETE):
${request.originalJobDescription}

OPTIMIZATION GUIDELINES:
- Keep ALL sections from the original (job summary, responsibilities, requirements, benefits, etc.)
- Only adjust language and tone to be more appealing to: ${selectedAudiences.map(a => a.name).join(', ')}
- Maintain the same length and detail level
- Ensure the optimized version is inclusive and professional
- Track specific changes made

Return ONLY this JSON format:
{
  "optimizedJobDescription": "[COMPLETE optimized job description with ALL original sections preserved]",
  "changes": [{"section": "section name", "original": "original text", "optimized": "optimized text", "reason": "why this change appeals to target audience", "audienceTarget": "audience name"}],
  "audienceScore": 85,
  "inclusivityScore": 90,
  "recommendations": ["specific tip 1", "specific tip 2"],
  "summary": "brief summary of optimization approach"
}`;
}

// Quick audience analysis for existing job descriptions
export async function analyzeJobDescriptionAudience(
  jobDescription: string,
  jobTitle: string
): Promise<{
  currentAudienceAppeal: Array<{
    audienceId: string;
    audienceName: string;
    appealScore: number;
    issues: string[];
    strengths: string[];
  }>;
  overallInclusivity: number;
  recommendations: string[];
}> {
  try {
    const prompt = `Analyze the audience appeal and inclusivity of this job description.

JOB TITLE: ${jobTitle}

COMPLETE JOB DESCRIPTION:
${jobDescription}

Analyze how well this job description appeals to different audiences. Rate each audience appeal from 0-100 and provide specific feedback.

Return ONLY this JSON format:
{
  "currentAudienceAppeal": [
    {"audienceId": "graduates", "audienceName": "Recent Graduates", "appealScore": 75, "issues": ["specific issue 1", "specific issue 2"], "strengths": ["specific strength 1", "specific strength 2"]},
    {"audienceId": "professionals", "audienceName": "Experienced Professionals", "appealScore": 80, "issues": ["issue"], "strengths": ["strength"]},
    {"audienceId": "women_friendly", "audienceName": "Women-Inclusive", "appealScore": 70, "issues": ["issue"], "strengths": ["strength"]},
    {"audienceId": "diverse_backgrounds", "audienceName": "Diverse Backgrounds", "appealScore": 75, "issues": ["issue"], "strengths": ["strength"]}
  ],
  "overallInclusivity": 80,
  "recommendations": ["specific recommendation 1", "specific recommendation 2", "specific recommendation 3"]
}`;

    const response = await optimizedCompletion(
      'audience-targeting',
      [
        {
          role: 'system',
          content: 'You are an expert diversity and inclusion consultant. Analyze job descriptions to determine how well they appeal to different target audiences and assess their overall inclusivity. Provide specific, actionable feedback. Respond with valid JSON only.'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      { useCache: true }
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No response from AI');
    }

    let cleanedContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    
    // Find JSON boundaries if there's extra text
    const jsonStart = cleanedContent.indexOf('{');
    const jsonEnd = cleanedContent.lastIndexOf('}');
    
    if (jsonStart !== -1 && jsonEnd !== -1 && jsonStart < jsonEnd) {
      cleanedContent = cleanedContent.substring(jsonStart, jsonEnd + 1);
    }
    
    try {
      const result = JSON.parse(cleanedContent);
      
      // Validate and fix required fields
      if (!Array.isArray(result.currentAudienceAppeal)) {
        result.currentAudienceAppeal = [];
      }
      
      if (typeof result.overallInclusivity !== 'number') {
        result.overallInclusivity = 75;
      }
      
      if (!Array.isArray(result.recommendations)) {
        result.recommendations = [];
      }
      
      return {
        currentAudienceAppeal: result.currentAudienceAppeal,
        overallInclusivity: result.overallInclusivity,
        recommendations: result.recommendations.filter(Boolean)
      };
    } catch (parseError) {
      console.error('Audience analysis JSON parsing error:', parseError);
      console.error('Raw content preview:', content.substring(0, 500) + '...');
      
      // Return a fallback response instead of throwing an error
      return {
        currentAudienceAppeal: [
          {
            audienceId: 'general',
            audienceName: 'General Audience',
            appealScore: 70,
            issues: ['Analysis could not be completed'],
            strengths: ['Standard job posting format']
          }
        ],
        overallInclusivity: 75,
        recommendations: [
          'Review language for inclusivity',
          'Consider diverse candidate needs',
          'Add welcoming company culture information'
        ]
      };
    }
  } catch (error) {
    console.error('Audience analysis error:', error);
    throw new ApiError(
      error instanceof Error 
        ? `Audience analysis failed: ${error.message}` 
        : 'Failed to analyze job description audience appeal', 
      500
    );
  }
} 